import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_TextToVideo = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/text-to-video',
			name: 'Text to Video AI — Turn Prompts into Clips by Lift',
			description:
				'Convert text to video with AI in a few clicks. Type your prompt, pick a video model, set the ratio and duration, and get a polished clip from your words. Try Lift today!',
			url: 'https://liftapp.ai/text-to-video',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/text-to-video#product' },
				{ '@id': 'https://liftapp.ai/text-to-video#howto' },
				{ '@id': 'https://liftapp.ai/text-to-video#benefits' },
				{ '@id': 'https://liftapp.ai/text-to-video#faq' },
				{ '@id': 'https://liftapp.ai/text-to-video#why-lift' },
				{ '@id': 'https://liftapp.ai/text-to-video#explore-tools' },
				{ '@id': 'https://liftapp.ai/text-to-video#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/text-to-video#product',
			name: 'Text to Video AI',
			description:
				'Convert text to video with AI in a few clicks. Type your prompt, pick a video model, set the ratio and duration, and get a polished clip from your words.',
			url: 'https://liftapp.ai/text-to-video',
			fileFormat: ['video/mp4'],
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/text-to-video#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Type Your Text Prompt',
					text: 'Describe the video you want in your own words, in any language — a scene, an ad concept, a story moment.',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Improve Your Prompt',
					text: 'Tap Improve Prompt and AI expands your text into a richer description for a more cinematic result.',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Choose A Model And Settings',
					text: 'Pick an AI video model, then set the aspect ratio, resolution, duration, and audio — or add a reference image to guide the look.',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download And Use',
					text: 'Download your video or publish it straight to your social feeds.',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/text-to-video#benefits',
			name: 'Who can benefit from AI Text to Video Generator?',
			description:
				"Lift's Text to Video AI helps content creators, marketers, writers, educators, and casual creators turn written prompts into ready-to-share video clips.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Content creators — Go from idea to finished clip in one sitting. Generate scroll-stopping videos for Instagram, TikTok, and YouTube without cameras or editing software.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Marketers & SMM managers — Script an ad concept in a sentence and see it on screen minutes later. Iterate on messaging by rewriting the prompt, not reshooting.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Writers & storytellers — Turn scenes from your stories into moving visuals for trailers, teasers, and social posts. If you can write it, you can watch it.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Educators & explainers — Illustrate processes and ideas that are hard to film with custom clips generated from a plain description.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Casual creators — Birthday greetings, inside jokes, dream scenes — type the idea and share the result, no skills required.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'Text to Video AI Examples by Lift',
				description:
					'AI videos generated from text prompts with the Lift text to video generator.',
			},
		},
		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/text-to-video#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift Text to Video AI?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift Text to Video AI is a premium online tool that converts a written prompt into a short video clip. It pairs your words with leading AI video models to render motion, detail, and atmosphere exactly as described.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift Text to Video AI work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Type your prompt in the text to video generator, then choose a model and adjust the ratio, resolution, duration, and audio. Hit Generate and get your clip in moments — then Regenerate for a fresh take whenever you want.',
					},
				},
				{
					'@type': 'Question',
					name: 'How do I write a good prompt for text to video AI?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Describe the subject, the motion, and the mood — camera direction helps too, like a slow zoom or an aerial shot. Short on time? Write a rough idea and tap Improve Prompt to turn it into a detailed description automatically.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All videos generated in Lift can be exported without watermark when you're on the Pro plan. Download them in high resolution — ready for social media, ads, or presentations.",
					},
				},
				{
					'@type': 'Question',
					name: 'Is it safe to upload my photos to Lift?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes — Lift processes your photos securely and uses them only to generate your results.',
					},
				},
			],
		},
		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/text-to-video#why-lift',
			name: 'Why Lift Text to Video AI stands out',
			description:
				"Lift's Text to Video AI combines professional-grade results, intelligent rendering, cross-platform access, and affordability.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Prompt to Video AI Generator by Lift',
				description: 'Prompt to video generation with Lift AI.',
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/text-to-video#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			itemListElement: [...exploreSchemaItemList.itemListElement],
		},
		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/text-to-video#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},
		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Text to Video AI',
					item: 'https://liftapp.ai/text-to-video',
				},
			],
		},
	],
};
